import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { filterValues } from "@prismicio/editor-support/Object"
import {
  Document,
  SliceItemContent,
} from "@prismicio/types-internal/lib/content"
import {
  type InfiniteData,
  type QueryClient,
  queryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"

const authorSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().optional(),
  uploadedAvatar: z.string().optional(),
})

export const languageSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    is_master: z.boolean().optional(),
  })
  .transform((language) => ({
    id: language.id,
    label: language.name,
    ...(language.is_master !== undefined
      ? { isMaster: language.is_master }
      : {}),
  }))

const documentVersionStatusSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("unclassified") }),
  z.object({ status: z.literal("published") }),
  z.object({ status: z.literal("release"), release_id: z.string() }),
  z.object({ status: z.literal("archived") }),
])

// document version static fields between Core API and Search API
const versionCommonFieldsSchema = z.object({
  version_id: z.string(),
  tags: z.array(z.string()),
  preview_image: z.string().optional(),
  last_modified_date: z.coerce.date(),
  uid: z.string().optional(),
})

// document version fields for Core API
const documentVersionSchema = documentVersionStatusSchema.and(
  versionCommonFieldsSchema.extend({
    custom_type_label: z.string(),
    preview_summary: z.string().optional(),
    author: authorSchema,
  }),
)

// document version fields for Search API
const documentSearchVersionSchema = documentVersionStatusSchema.and(
  versionCommonFieldsSchema.extend({
    custom_type_id: z.string().optional(),
    summary: z.string().optional(),
    author_ids: z.array(z.string()).optional(),
    highlights: z
      .object({
        uid: z.string().optional(),
      })
      .optional(),
  }),
)

export function isDocumentVersion(
  value: unknown,
): value is z.infer<typeof documentVersionSchema> {
  return documentVersionSchema.safeParse(value).success
}

export function isDocumentSearchVersion(
  value: unknown,
): value is z.infer<typeof documentSearchVersionSchema> {
  return documentSearchVersionSchema.safeParse(value).success
}

/**
 * Schema for the title of a document.
 * If the title is `null` or `undefined`, it will be transformed to "Untitled".
 * @example documentTitleSchema.parse(undefined) // "Untitled"
 *
 * Historically we have allowed documents without titles, but we want to enforce that all documents have a title now.
 * This schema is used to ensure that all documents have a title and does not break when none is given. This helps us keep consistent UIs.
 */
const documentTitleSchema = z
  .string()
  .nullish()
  .transform((title) => title ?? "Untitled")

export const documentMetaSchema = z.object({
  id: z.string(),
  title: documentTitleSchema,
  custom_type_id: z.string(),
  group_lang_id: z.string(),
  language: languageSchema,
  versions: z.array(documentVersionSchema),
})

const getDocumentListSchema = z.object({
  total: z.number(),
  cursor: z.string().optional(),
  results: z.array(documentMetaSchema),
})

const documentSearchDocumentSchema = z
  .object({
    id: z.string(),
    title: documentTitleSchema,
    custom_type_id: z.string(),
    group_lang_id: z.string(),
    interesting_versions: z.array(documentSearchVersionSchema),
    locale_id: z.string().optional(),
  })
  .transform((value) => {
    // align the structure with the old core API
    const { interesting_versions: versions, ...rest } = value
    return {
      versions,
      ...rest,
    }
  })

const documentsFullTextSearchAPISchemaOkType = z.object({
  results: z.array(documentSearchDocumentSchema),
})

export type Language = z.TypeOf<typeof languageSchema>
export type GetDocumentListSchema = z.TypeOf<typeof getDocumentListSchema>
export type DocumentFullTextSearchApi = z.TypeOf<
  typeof documentsFullTextSearchAPISchemaOkType
>
export type DocumentMeta = z.TypeOf<typeof documentMetaSchema>
export type DocumentSearchMeta = z.TypeOf<typeof documentSearchDocumentSchema>
export type DocumentVersion = z.TypeOf<typeof documentVersionSchema>
export type DocumentStatus = DocumentVersion["status"]

export interface GetDocumentListArgs {
  baseUrl: URL
  authStrategy: AuthStrategy
  signal?: AbortSignal | null | undefined

  limit?: number
  cursor?: string
  statuses?: string[]
  authors?: string[]
  language?: string
  groupLangIds?: string[]
  documentIds?: string[]
  customTypes?: string[]
  tags?: string[]
  term?: string
  uids?: string[]
}

export function getDocumentList(
  args: GetDocumentListArgs,
): Promise<GetDocumentListSchema> {
  const {
    baseUrl,
    authStrategy,
    limit,
    cursor,
    statuses,
    authors,
    language,
    groupLangIds,
    documentIds,
    customTypes,
    tags,
    term,
    uids,
    signal,
  } = args

  const url = new URL("documents/search", baseUrl)

  const body = filterValues(
    {
      statuses,
      authors,
      language,
      groupLangIds,
      customTypes,
      tags,
      term,
      documentIds,
      limit,
      cursor,
      uids,
    },
    (value) => {
      if (value === "") return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    },
  )

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: getDocumentListSchema,
    signal,
    method: "POST",
    body,
  })
}

export function searchFullTextDocuments(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  queryString: string,
  options?: Pick<RequestInit, "signal">,
): Promise<DocumentFullTextSearchApi> {
  const url = new URL(baseUrl)
  url.search = queryString

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: documentsFullTextSearchAPISchemaOkType,
    ...options,
  })
}

export function makeSearchFullTextDocumentsQueryString(args: {
  repository: string
  searchTerm?: string
  tags?: string[]
  authors?: string[]
  status?: string[]
  localeId?: string
  customTypesIds?: string[]
}): string {
  const {
    repository,
    searchTerm,
    tags,
    authors,
    status,
    localeId,
    customTypesIds,
  } = args
  const searchParams = new URLSearchParams()
  searchParams.append("repository", repository)

  if (searchTerm) {
    searchParams.append("searchTerm", searchTerm)
  }
  if (tags?.length) {
    searchParams.append("tags", tags.toString())
  }
  if (authors?.length) {
    searchParams.append("authors", authors.toString())
  }
  if (status?.length) {
    searchParams.append("status", status.toString())
  }
  if (localeId) {
    searchParams.append("localeId", localeId)
  }
  if (customTypesIds?.length) {
    searchParams.append("customTypesIds", customTypesIds.toString())
  }

  return searchParams.toString()
}

type GetDocumentsQueryFilters =
  | "tags"
  | "language"
  | "customTypes"
  | "status"
  | "term"
  | "group_lang_id"
  | "document_id"
  | "uid"
  | "authors"

export type SearchDocumentParams = {
  [S in GetDocumentsQueryFilters]?: string[]
}

export function createSearchParams<T extends string>(
  params: { name: T; values: (string | undefined)[] }[],
): [string, string][] {
  return params.reduce<[string, string][]>((queryParams, currentVariable) => {
    // if current variable has no values, return queryParams as is without adding the current empty variable
    if (
      currentVariable.values.every(
        (value) => value === "" || value === undefined,
      )
    )
      return queryParams

    const arr = currentVariable.values.map<[string, string]>((value) => [
      currentVariable.name,
      value ?? "",
    ])
    return queryParams.concat(arr)
  }, [])
}

export function getDocumentById(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  id: string,
): Promise<DocumentMeta> {
  return request(new URL(`documents/${id}`, baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: documentMetaSchema,
  })
}

export const documentSchema = z.custom().transform((value, context) => {
  const document = Document.decode(value)
  if (document._tag === "Left") {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid document.`,
    })
    return z.NEVER
  }
  return document.right
})

export const sliceItemContentSchema = z.unknown().transform((val, ctx) => {
  const result = SliceItemContent.decode(val)

  // Either.Right = Success
  if (result._tag === "Right") return result.right

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Invalid slice content.",
  })

  return z.NEVER
})

export const documentKeys = {
  all: () => ["documents"] as const,
  lists: () => [...documentKeys.all(), "list"] as const,
  list: (args: GetDocumentListArgs) => [...documentKeys.lists(), args] as const,
  infiniteLists: () => [...documentKeys.all(), "list:infinite"] as const,
  infiniteList: (args: GetDocumentListArgs) =>
    [...documentKeys.infiniteLists(), args] as const,
}

/**
 * Returns updated document or undefined if document should be removed
 */
type UpdateDocumentFunction = (
  document: DocumentMeta,
  page: GetDocumentListSchema,
) => DocumentMeta | undefined

export function updateDocumentListData(
  queryClient: QueryClient,
  updateFunction: UpdateDocumentFunction,
) {
  queryClient.setQueriesData(
    {
      queryKey: documentKeys.lists(),
    },
    (oldData: GetDocumentListSchema | undefined) => {
      if (!oldData) return oldData
      return updateDocumentListPage(oldData, updateFunction)
    },
  )
  queryClient.setQueriesData(
    {
      queryKey: documentKeys.infiniteLists(),
    },
    (oldData: InfiniteData<GetDocumentListSchema> | undefined) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        pages: oldData.pages.map((page) =>
          updateDocumentListPage(page, updateFunction),
        ),
      }
    },
  )
}

function updateDocumentListPage(
  page: GetDocumentListSchema,
  updateFunction: UpdateDocumentFunction,
) {
  const results = page.results.flatMap((document) => {
    const newDocument = updateFunction(document, page)
    return newDocument ? [newDocument] : []
  })

  return {
    ...page,
    results,
    total: page.total + (results.length - page.results.length),
  }
}

export async function invalidateDocumentListData(queryClient: QueryClient) {
  // Infinite lists depend on regular lists, so in order to fetch them correctly we need to do these one after another
  await queryClient.invalidateQueries({
    queryKey: documentKeys.lists(),
  })
  await queryClient.invalidateQueries({
    queryKey: documentKeys.infiniteLists(),
  })
}

export function useDocumentList(args: GetDocumentListArgs) {
  return useSuspenseQuery(getDocumentListOptions(args))
}

export function getDocumentListOptions(args: GetDocumentListArgs) {
  return queryOptions({
    queryKey: documentKeys.list(args),
    queryFn: ({ queryKey: [, , params] }) => getDocumentList(params),
  })
}
